import React from 'react';
import { AlertCircle, CheckCircle2, Key } from 'lucide-react';
import { FormModal, FormModalProps } from './FormModal';
import { Input } from '../form/Input';

export interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface PasswordErrors {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ChangePasswordModalProps extends Omit<FormModalProps, 'onSubmit' | 'children'> {
  passwordData: PasswordData;
  passwordErrors: PasswordErrors;
  onPasswordChange: (field: keyof PasswordData, value: string) => void;
  onSubmit: () => void;
  passwordChangeStatus?: 'idle' | 'success' | 'error';
  /**
   * What went wrong, when the caller knows. Failures the server can name (the
   * password was reused, too many attempts, the account has no password) are
   * more useful than "please try again", which invites the operator to repeat
   * the thing that just failed. Falls back to the generic line.
   */
  errorMessage?: string;
  /** Overrides the requirements line under the new-password field. */
  passwordHint?: string;
}

const DEFAULT_PASSWORD_HINT =
  'Password must be at least 8 characters and include uppercase, lowercase, a number and a special character';

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  passwordData,
  passwordErrors,
  onPasswordChange,
  onSubmit,
  passwordChangeStatus = 'idle',
  errorMessage,
  passwordHint = DEFAULT_PASSWORD_HINT,
  isLoading = false,
  ...props
}) => {
  const validateForm = () => {
    const hasAllFields = !!(
      passwordData.currentPassword &&
      passwordData.newPassword &&
      passwordData.confirmPassword
    );
    const noErrors = !passwordErrors.currentPassword && !passwordErrors.newPassword && !passwordErrors.confirmPassword;
    return hasAllFields && noErrors;
  };

  return (
    <FormModal
      {...props}
      title="Change Password"
      onSubmit={onSubmit}
      submitText="Change Password"
      loadingText="Changing..."
      isLoading={isLoading}
      canSubmit={validateForm() && passwordChangeStatus !== 'success'}
      size="md"
    >
      <div className="space-y-4">
        {/* Current Password */}
        <div className="space-y-2">
          <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Current Password
          </label>
          <Input
            id="currentPassword"
            type="password"
            value={passwordData.currentPassword}
            onChange={(value) => onPasswordChange('currentPassword', value)}
            placeholder="Enter your current password"
            disabled={isLoading}
            autoComplete="current-password"
            leftIcon={Key}
          />
          {passwordErrors.currentPassword && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {passwordErrors.currentPassword}
            </p>
          )}
        </div>

        {/* New Password */}
        <div className="space-y-2">
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            New Password
          </label>
          <Input
            id="newPassword"
            type="password"
            value={passwordData.newPassword}
            onChange={(value) => onPasswordChange('newPassword', value)}
            placeholder="Enter your new password"
            disabled={isLoading}
            autoComplete="new-password"
            leftIcon={Key}
          />
          {passwordErrors.newPassword && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {passwordErrors.newPassword}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {passwordHint}
          </p>
        </div>

        {/* Confirm Password */}
        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirm New Password
          </label>
          <Input
            id="confirmPassword"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(value) => onPasswordChange('confirmPassword', value)}
            placeholder="Confirm your new password"
            disabled={isLoading}
            autoComplete="new-password"
            leftIcon={Key}
          />
          {passwordErrors.confirmPassword && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {passwordErrors.confirmPassword}
            </p>
          )}
        </div>

        {/* Status Messages */}
        {passwordChangeStatus === 'success' && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-800 dark:text-green-200">
                Password changed!
              </span>
            </div>
          </div>
        )}

        {passwordChangeStatus === 'error' && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-800 dark:text-red-200">
                {errorMessage || 'Failed to change password. Please try again.'}
              </span>
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};
